import React, { useMemo, useState } from "react";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import { trpcClient } from "@/lib/trpc";
import { PrintLayoutForm } from "./PrintLayoutForm";

export default function LayoutFormPage() {
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as { layoutId?: string };
  const search = useSearch({ strict: false }) as {
    detailIds?: string | string[];
    orderId?: string;
    orderCode?: string;
    focusField?: "code" | "sheetSize" | "ups" | "sheets";
  };

  const isEdit = Boolean(params.layoutId);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const parsedDetailIds = useMemo(() => {
    const raw = search?.detailIds;
    if (!raw) return [];
    const list = Array.isArray(raw) ? raw.flatMap((x) => String(x).split(",")) : String(raw).split(",");
    return Array.from(new Set(list.map((s) => s.trim()).filter(Boolean)));
  }, [search?.detailIds]);

  const { data: prefillData, isLoading: isPrefillLoading } = useQuery({
    queryKey: ["prepress-form-prefill", parsedDetailIds.join(",")],
    queryFn: () =>
      trpcClient.prepress.listPendingOrderDetails.query({
        ids: parsedDetailIds,
        includeExisting: true,
        limit: 100,
      }),
    enabled: !isEdit && parsedDetailIds.length > 0,
  });

  const initialDetails = useMemo(() => {
    return (prefillData as any)?.items || [];
  }, [prefillData]);

  return (
    <StandardDetailLayout
      breadcrumbs={[
        { label: "Bình file", to: "/layouts" },
        { label: isEdit ? "Chỉnh sửa bài bình" : "Tạo bài bình mới" },
      ]}
      title={isEdit ? "Chỉnh sửa Bài Bình File" : "Tạo Bài Bình File"}
      showContextPanel={false}
      onBack={() => navigate({ to: "/layouts", replace: true })}
      actions={
        <div className="flex items-center gap-2 shrink-0">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isSubmitting}
            onClick={() => navigate({ to: "/layouts", replace: true })}
            className="h-8 text-xs"
          >
            Hủy
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={isSubmitting}
            onClick={() => {
              const submitBtn = document.querySelector(
                '[data-testid="canonical-print-layout-form"] button[type="submit"]',
              ) as HTMLButtonElement | null;
              submitBtn?.click();
            }}
            className="h-8 text-xs bg-indigo-600 hover:bg-indigo-700 text-white font-semibold"
          >
            {isSubmitting ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
            )}
            {isEdit ? "Cập nhật Bài Bình" : "Lưu Bài Bình"}
          </Button>
        </div>
      }
    >
      {!isEdit && parsedDetailIds.length > 0 && isPrefillLoading ? (
        <div className="flex items-center justify-center py-16 text-slate-500 text-sm gap-2">
          <Loader2 className="h-5 w-5 animate-spin text-indigo-600" />
          <span>Đang nạp chi tiết đơn hàng...</span>
        </div>
      ) : (
        <PrintLayoutForm
          mode={isEdit ? "edit" : "create"}
          layoutId={params.layoutId || null}
          initialDetails={initialDetails}
          orderId={search?.orderId || null}
          orderCode={search?.orderCode || null}
          focusField={search?.focusField || null}
          isDrawerMode={false}
          onSubmittingChange={setIsSubmitting}
          onCancel={() => navigate({ to: "/layouts", replace: true })}
          onSuccess={(res) => {
            if (!res.createPo) {
              navigate({
                to: "/layouts",
                search: { tab: "cho_tao_lenh" } as any,
                replace: true,
              });
            }
          }}
        />
      )}
    </StandardDetailLayout>
  );
}
